import React, { useMemo, useState } from 'react';
import { EntityEditFetcher } from '../components/EntityEditFetcher';
import typeIntro from '../static/introspection/Trend360.json';
import inputTypeIntro from '../static/introspection/Trend360CreateInput.json';
import { ENTITY } from '../utils/entities';
import { Trend360CreateMutation } from 'graphql/mutations/Trend360CreateMutation';
import { ACTIONS, generateUrl } from 'utils/urls';
import { ChoicesField } from 'components/fields/ChoicesField';
import { t } from 'utils/labels';
import { IChoice } from 'utils/misc';
import { generateBaseField, toChoices } from 'utils/form';
import {
	TREND360_TYPE_CHOICES,
	Trend360TypeValue,
	trend360CreateTransformer,
	trend360FieldsForType,
	trend360SaveHandlers,
} from 'utils/entities/trend360';

/**
 * Pagina di creazione Trend360.
 *
 * In testa viene chiesta la tipologia (macrotrend / trend potenziale / trend confermato);
 * solo dopo la scelta viene montato il form corrispondente (EntityEditFetcher con la
 * whitelist e il transformer della tipologia). Il cambio tipologia rimonta il form da
 * zero (key), previa conferma perche' scarta i valori inseriti.
 *
 * Le relazioni multiple (campi `rel_*`) non fanno parte della mutation di creazione:
 * vengono gestite dalla coppia di handler di trend360SaveHandlers (beforeSave le mette
 * da parte, afterCreate le scrive via relationships360Bulk PRIMA del redirect).
 *
 * NB - la rotta generica di clonazione (/trend360s/:id/clone) porta qui con `id`
 * valorizzato ma la clonazione non e' supportata (il dettaglio ha clonable={false}):
 * il ripristino dei gruppi ripetibili JSON e delle relazioni in un form pre-compilato
 * non e' ancora implementato, quindi l'id viene ignorato e si crea da zero.
 */
const Trend360Create: React.FC<{ id?: string }> = () => {
	const [typeChoice, setTypeChoice] = useState<IChoice | null>(null);

	const trendType = typeChoice
		? (typeChoice.value as Trend360TypeValue)
		: null;

	// il campo del selettore di tipologia e i gestori del form corrente: stabili
	// finche' non cambia la tipologia (evitano churn di identita' ad ogni render)
	const typeSelectorField = useMemo(
		() =>
			toChoices(TREND360_TYPE_CHOICES, true)(
				generateBaseField(
					'trendType',
					t`Trend360/trendType/label` + '*',
					t`Trend360/trendType/helpText`,
				),
			),
		[],
	);
	const saveHandlers = useMemo(
		() => (trendType ? trend360SaveHandlers(trendType) : null),
		[trendType],
	);
	const transformer = useMemo(
		() => (trendType ? trend360CreateTransformer(trendType) : null),
		[trendType],
	);

	return (
		<>
			<ChoicesField
				field={{ ...typeSelectorField, value: typeChoice } as any}
				path={'trendType'}
				changeHandler={(value: any) => {
					// il cambio tipologia rimonta il form scartando i valori inseriti
					if (
						typeChoice &&
						(!value || value.value !== typeChoice.value) &&
						!window.confirm(t`Trend360/typeSwitch/confirm`)
					) {
						return;
					}
					setTypeChoice(value);
				}}
			/>
			{trendType && saveHandlers && transformer && (
				<EntityEditFetcher
					key={trendType}
					fetcher={null}
					create={true}
					mutation={Trend360CreateMutation}
					typeIntro={typeIntro}
					inputTypeIntro={inputTypeIntro}
					entityName={ENTITY.TREND_360}
					customDetailUrl={(newId) =>
						generateUrl(ENTITY.TREND_360, ACTIONS.DETAIL, newId) +
						'?flow=postcreate'
					}
					fields={trend360FieldsForType(trendType)}
					transformer={transformer}
					beforeSaveTransformer={saveHandlers.beforeSaveTransformer}
					afterSave={saveHandlers.afterCreate}
				/>
			)}
		</>
	);
};

export { Trend360Create };
